import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface Subscription {
  id: string;
  customer: {
    id: string;
    email: string;
    name: string;
  } | null;
  plan: {
    id: string;
    product: string;
    nickname: string | null;
    amount: number;
    currency: string;
    interval: string;
  } | null;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  created: number;
}

interface Invoice {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  due_date: number | null;
  created: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

interface SubscriptionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
}

export const SubscriptionDetailsModal: React.FC<SubscriptionDetailsModalProps> = ({
  open,
  onClose,
  subscription,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Fetch invoices for this subscription
  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['subscription-invoices', subscription?.id],
    queryFn: async () => {
      if (!subscription) return null;
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/subscriptions/${subscription.id}/invoices`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch invoices');
      }
      return response.json();
    },
    enabled: open && !!subscription,
  });

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Get status color
  const getStatusColor = (status: string): 'success' | 'default' | 'error' | 'primary' | 'warning' => {
    switch (status) {
      case 'active':
      case 'paid':
        return 'success';
      case 'canceled':
      case 'void':
        return 'default';
      case 'past_due':
      case 'uncollectible':
        return 'error';
      case 'trialing':
      case 'open':
        return 'primary';
      case 'incomplete':
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!subscription) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">Subscription Details</Typography>
          <Typography variant="body2" color="text.secondary">
            {subscription.id}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Subscription Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Subscription Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Customer
              </Typography>
              <Typography variant="body2">
                {subscription.customer?.email || 'N/A'}
              </Typography>
              {subscription.customer?.name && (
                <Typography variant="caption" color="text.secondary">
                  {subscription.customer.name}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Plan
              </Typography>
              <Typography variant="body2">
                {subscription.plan?.nickname || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box>
                <Chip
                  label={subscription.status}
                  color={getStatusColor(subscription.status)}
                  size="small"
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body2">
                {subscription.plan
                  ? `${formatCurrency(subscription.plan.amount, subscription.plan.currency)} / ${subscription.plan.interval}`
                  : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Period
              </Typography>
              <Typography variant="body2">
                {formatDate(subscription.current_period_start)} -{' '}
                {formatDate(subscription.current_period_end)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">{formatDate(subscription.created)}</Typography>
            </Box>
            {subscription.cancel_at_period_end && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cancellation
                </Typography>
                <Typography variant="body2" color="warning.main">
                  Will cancel at period end
                </Typography>
              </Box>
            )}
            {subscription.canceled_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Canceled At
                </Typography>
                <Typography variant="body2">{formatDate(subscription.canceled_at)}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Invoices */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Invoices ({invoicesData?.data?.invoices?.length || 0})
          </Typography>

          {isLoadingInvoices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoicesData?.data?.invoices && invoicesData.data.invoices.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount Due</TableCell>
                    <TableCell align="right">Amount Paid</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoicesData.data.invoices.map((invoice: Invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {invoice.number || invoice.id.substring(0, 12)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          color={getStatusColor(invoice.status || 'default')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(invoice.amount_due, invoice.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(invoice.amount_paid, invoice.currency)}
                      </TableCell>
                      <TableCell>
                        {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                      </TableCell>
                      <TableCell>{formatDate(invoice.created)}</TableCell>
                      <TableCell>
                        {invoice.hosted_invoice_url && (
                          <Link
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <OpenInNew fontSize="small" />
                            View
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No invoices found for this subscription.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
